// hooks/api/student.queries.ts
import { getApiErrorMessage } from "@/lib/apiError";
import { notifyError, notifySuccess, notifyWarning } from "@/lib/notification";
import * as studentService from "@/services/student.service";
import {
  BatchStudentParamsType,
  StudentCreateType,
  StudentParamsType,
  StudentType,
  StudentUpdateType,
} from "@/services/student.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const studentsKey = {
  all: ["students"] as const,
  lists: (filters: StudentParamsType) =>
    [...studentsKey.all, "lists", filters] as const,
  detail: (id: string) => [...studentsKey.all, "detail", id] as const,
  byBatch: (params: BatchStudentParamsType) =>
    [...studentsKey.all, "batch", params] as const,
};

export const useStudentsQuery = (filters: StudentParamsType) =>
  useQuery({
    queryKey: studentsKey.lists(filters),
    queryFn: () => studentService.fetchAllStudents(filters),
    placeholderData: (prev) => prev,
  });

export const useStudentsCountQuery = () =>
  useQuery({
    queryKey: [...studentsKey.all, "count"] as const,
    queryFn: () => studentService.fetchStudentsCount(),
  });

export const useStudentByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: studentsKey.detail(id),
    queryFn: () => studentService.fetchStudentById(id),
    enabled: enabled && !!id,
  });

export const useCreateStudentMutation = (filters: StudentParamsType) => {
  const queryClient = useQueryClient();
  return useMutation<StudentType, AxiosError, StudentCreateType>({
    mutationFn: (data) => studentService.createStudent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: studentsKey.lists(filters) });
      notifySuccess("Student added successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to add student"),
  });
};

export const useCreateBatchStudentMutation = (batchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof studentService.createRealStudent>[0]
    ) => studentService.createRealStudent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["batches", "detail", batchId],
      });
      queryClient.invalidateQueries({ queryKey: studentsKey.all }); // Added to refresh student list
      notifySuccess("Student added successfully");
    },
    onError: (error: any) =>
      notifyError(getApiErrorMessage(error, "Failed to add student")),
  });
};

export const useUpdateStudentMutation = (filters: StudentParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: StudentCreateType }) =>
      studentService.updateStudent(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: studentsKey.lists(filters) });
      queryClient.invalidateQueries({
        queryKey: studentsKey.detail(variables._id),
      });
      notifySuccess("Student updated successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update student"),
  });
};

export const useDeleteStudentMutation = (filters: StudentParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => studentService.deleteStudent(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({ queryKey: studentsKey.lists(filters) });
      queryClient.removeQueries({ queryKey: studentsKey.detail(_id) });
      notifySuccess("Student deleted successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to delete student"),
  });
};

export const useStudentsByBatchQuery = (
  params: BatchStudentParamsType,
  enabled = true
) =>
  useQuery({
    queryKey: studentsKey.byBatch(params),
    queryFn: () => studentService.fetchStudentsByBatch(params),
    enabled: enabled && !!params.batch_id,
    placeholderData: (prev) => prev,
  });

export const useUpdateStudentDetailMutation = (studentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StudentUpdateType) =>
      studentService.updateStudentDetail(studentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: studentsKey.detail(studentId),
      });
      queryClient.invalidateQueries({ queryKey: studentsKey.all });
      notifySuccess("Student updated successfully");
    },
    onError: (error: any) =>
      notifyError(getApiErrorMessage(error, "Failed to update student")),
  });
};

export const useDeleteStudentDetailMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => studentService.deleteStudentDetail(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: studentsKey.all });
      queryClient.removeQueries({ queryKey: studentsKey.detail(id) });
      notifySuccess("Student deleted successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to delete student"),
  });
};

export const useBulkUploadStudentsMutation = (batchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      params: Parameters<typeof studentService.bulkUploadStudents>[0]
    ) => studentService.bulkUploadStudents(params),
    // The endpoint returns 200 even when rows fail — the real result is in the
    // body ({ created, skipped, errors }), so we report it from onSuccess.
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: studentsKey.all });
      queryClient.invalidateQueries({
        queryKey: ["batches", "detail", batchId],
      });

      const { created, skipped, errors } = result;
      const errorDetail = errors
        .slice(0, 5)
        .map((e) => `Row ${e.row}: ${e.reason}`)
        .join("\n");

      const parts = [`Added ${created}`];
      if (skipped)
        parts.push(`skipped ${skipped} duplicate${skipped > 1 ? "s" : ""}`);
      if (errors.length) parts.push(`${errors.length} failed`);
      const summary = parts.join(", ");

      if (created > 0) {
        if (errors.length) notifyWarning(summary, errorDetail);
        else notifySuccess(summary);
      } else if (errors.length) {
        notifyError("No students added — all rows failed", errorDetail);
      } else if (skipped > 0) {
        notifyWarning(
          `All ${skipped} row${skipped > 1 ? "s were" : " was"} duplicate — nothing new added`
        );
      } else {
        notifyWarning("No students were added (empty file?)");
      }
    },
    onError: (error: any) =>
      notifyError(getApiErrorMessage(error, "Bulk upload failed")),
  });
};
